import React from 'react';
import { withRouter, useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import ArticleListPage, {
  ArticlesListCheckboxWrapper,
} from '@fuww/library/src/Layouts/ArticleListPage';
import Checkbox from '@fuww/library/src/Checkbox';
import CategoryFilter from
  '@fuww/library/src/ProductFilters/CategoryFilter';
import { useIntl } from 'react-intl';
import HiddenLoader from '@fuww/library/src/HiddenLoader';
import ArticleList from '../components/News/Article/List';
import HomeLink from '../components/HomeLink';
import LinkAndAnchor from '../components/LinkAndAnchor';
import withQuery from '../lib/withQuery';
import { useSiteContext } from '../components/SiteContext';
import articlesConnectionQuery
  from '../lib/queries/news/articlesConnectionQuery.graphql';
import {
  newsFeedCategoryIds,
  newsAndPressFeedCategoryIds,
} from '../lib/mainCategoryIds';
import loading from '../lib/loading';
import { newsFeedImageConfigurations } from '../lib/imageConfigurations';
import Head from '../components/Head';
import messages from '../lib/messages.json';
import redirectToNewsDomain from '../lib/redirectToNewsDomain';
import withApollo from '../lib/apollo';
import replaceRoute from '../lib/replaceRoute';
import correctLocalizedRoute from '../lib/correctLocalizedRoute';
import throw404 from '../lib/throw404';
import MostRead from '../components/News/ArticleFeed/MostReadBlock';

const ArticleListWithQuery = withQuery(ArticleList, { renderAlways: true });

const DynamicSideBar = dynamic(() => import(
  '../components/News/Article/SideBar'
), {
  ssr: false,
  loading,
});

const errorMessage = 'Error loading news articles...';

const categories = {
  fashion: { news: 8, press: 29 },
  retail: { news: 9, press: 30 },
  business: { news: 10, press: 31 },
  culture: { news: 11, press: 33 },
  people: { news: 12, press: 32 },
  fairs: { news: 13, press: 37 },
};

const getCategoryIds = (articleCategory, includePress) => {
  if (!articleCategory) {
    return includePress === 'true'
      ? newsAndPressFeedCategoryIds
      : newsFeedCategoryIds;
  }
  return includePress === 'true'
    ? Object.values(categories[articleCategory])
    : categories[articleCategory].news;
};

const NewsFeedPage = () => {
  const { query: { press, category } } = useRouter();
  const intl = useIntl();
  const {
    newsboard,
    locale,
  } = useSiteContext();

  if (category && !categories[category]) return throw404();

  const variables = {
    newsboard,
    pageviews: false,
    sort: 'CREATED_AT',
    first: 12,
    imageConfigurations: newsFeedImageConfigurations,
    categoryIds: getCategoryIds(category, press),
  };

  const breadcrumbs = [
    <HomeLink />,
    <LinkAndAnchor
      route={`news-feed.${locale}`}
      passHref
    >
      {intl.formatMessage(messages.newsFeedTitle)}
    </LinkAndAnchor>,
  ];

  return (
    <>
      <Head
        title={intl.formatMessage(messages.newsFeedTitle)}
        description={intl.formatMessage(messages.indexDescription)}
      />
      <ArticleListPage
        title={intl.formatMessage(messages.newsFeedTitle)}
        breadcrumbs={breadcrumbs}
        sidebar={(
          <DynamicSideBar />
        )}
        featuredBlock={(<MostRead />)}
        searchFields={(
          <>
            <CategoryFilter
              categories={
                [
                  {
                    name: intl.formatMessage(messages.all),
                    handleInteraction: () => replaceRoute(
                      `news-feed.${locale}`,
                      { press },
                    ),
                  },
                  ...Object.keys(categories).map((categoryName) => ({
                    name: intl.formatMessage(
                      messages[`category.news.${categoryName}`],
                    ),
                    handleInteraction: () => replaceRoute(
                      `news-feed.${locale}`,
                      { category: categoryName, press },
                    ),
                  })),
                ]
              }
              selectedCategory={
                category
                  ? intl.formatMessage(messages[`category.news.${category}`])
                  : intl.formatMessage(messages.all)
              }
              name="Filter"
            />
            <ArticlesListCheckboxWrapper>
              <Checkbox
                checked={press === 'true'}
                label={intl.formatMessage(messages.includePressReleases)}
                onChange={({ target: { checked } }) => {
                  replaceRoute(`news-feed.${locale}`, {
                    press: checked ? 'true' : 'false',
                    category,
                  });
                }}
              />
            </ArticlesListCheckboxWrapper>
          </>
        )}
      >
        <ArticleListWithQuery
          query={articlesConnectionQuery}
          variables={variables}
          errorMessage={errorMessage}
          loader={<HiddenLoader />}
        />
      </ArticleListPage>
    </>
  );
};

export const NewsFeed = withRouter(NewsFeedPage);

NewsFeed.getInitialProps = (context) => {
  const {
    req, res, query, asPath,
  } = context;

  redirectToNewsDomain(asPath)({ req, res, query });
  correctLocalizedRoute(context, 'news-feed');

  return {};
};

export default withApollo(NewsFeed);
